import { PointRuleActionType, isPointRuleActionWithoutAppPreview } from "../../../models/pointRule";

/** §3.3 — H3 de la escala (20px / 600 / 1.3), para los títulos de bloque del modal de reglas. */
export const POINT_RULE_BLOCK_TITLE_STYLE = {
  fontSize: "var(--font-h3-size)",
  fontWeight: "var(--font-h3-weight)",
  lineHeight: "var(--font-h3-line)",
} as const;

export interface PointRulePreviewProps {
  /** Para apagar la vista previa en las reglas que la app no dibuja (ej. `event_bonus`). */
  actionType: PointRuleActionType;
  /**
   * La app NO dibuja el ícono en la fila "Cómo ganar" (ver `PointsActionRow` en el repo
   * mobile), así que acá no se renderiza. Se mantiene en el contrato porque el ícono sigue
   * viviendo en el modelo y en la DB y lo vamos a volver a mostrar más adelante.
   */
  iconKey: string | null;
  title: string | null;
  description: string | null;
  /** Número con signo, como en la app: "+10", "−5" (o la leyenda de los montos variables). */
  valueLabel: string;
  isPenalty: boolean;
  visibleToUser: boolean;
}

/**
 * Cómo se ve la regla en la app ("Cómo ganar Chispas"). Solo presentación: recibe los
 * valores del formulario en vivo y no toca estado ni API.
 *
 * Copia la fila `PointsActionRow` del mobile: título y descripción a la izquierda y el valor
 * a la derecha como texto en el color de la institución (en rojo cuando es penalidad). Sin
 * ícono y sin pill, porque esa fila de la app no los tiene.
 */
export const PointRulePreview = ({
  actionType,
  title,
  description,
  valueLabel,
  isPenalty,
  visibleToUser,
}: PointRulePreviewProps) => {
  // Hay reglas que la app no dibuja (ej. `event_bonus`): no se previsualiza algo que no existe.
  if (isPointRuleActionWithoutAppPreview(actionType)) {
    return null;
  }

  // Ganar va en el color de marca; la penalidad, en rojo: el signo ya viene en `valueLabel`.
  const valueColor = isPenalty ? "var(--error-bg-dark)" : "var(--primary-color)";

  return (
    <section className="flex flex-column" style={{ gap: "var(--space-12)" }}>
      <h3 className="m-0" style={POINT_RULE_BLOCK_TITLE_STYLE}>
        Así se ve en la app
      </h3>
      {/* Fondo gris de "pantalla" para que la tarjeta se lea como un ítem de la lista de la app. */}
      <div
        style={{
          backgroundColor: "var(--section-head-bg)",
          borderRadius: "var(--radius-lg)",
          padding: "var(--space-16)",
        }}
      >
        {/* `minHeight` para que la tarjeta respire como una fila real de la lista de la app y
            no quede apretada cuando la regla no tiene descripción. */}
        <div
          className="surface-card shadow-1 flex align-items-start justify-content-between"
          style={{
            borderRadius: "var(--radius-lg)",
            padding: "var(--space-18) var(--space-16)",
            gap: "var(--space-12)",
            minHeight: "76px",
            opacity: visibleToUser ? 1 : 0.5,
          }}
        >
          <div
            className="flex flex-column"
            style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere", gap: "2px" }}
          >
            <span
              className="text-900"
              style={{ fontSize: "var(--font-body-size)", fontWeight: 600, lineHeight: 1.3 }}
            >
              {title ?? <span className="text-color-secondary">Sin título</span>}
            </span>
            {description && (
              <span
                className="text-600"
                style={{
                  fontSize: "var(--font-small-size)",
                  lineHeight: "var(--font-small-line)",
                }}
              >
                {description}
              </span>
            )}
          </div>
          {/* El valor a la derecha, como en la fila "Cómo ganar": texto en color, ni pill ni
              ícono. Puede envolver ("Definido en cada evento" es largo) sin comerse el título. */}
          <span
            style={{
              fontSize: "var(--font-body-size)",
              fontWeight: 700,
              lineHeight: 1.3,
              color: valueColor,
              textAlign: "right",
            }}
          >
            {valueLabel}
          </span>
        </div>
      </div>
      {!visibleToUser && (
        <small className="text-600 flex align-items-center" style={{ gap: "6px" }}>
          <i className="pi pi-eye-slash" aria-hidden="true" />
          Esta regla no se muestra en la app.
        </small>
      )}
    </section>
  );
};
